/**
 * setParent (W1-10a; DESIGN.md §5.5 "Parent", rpe-format F§10.4, rpe-editor-features E§4.16).
 *
 * Phira: a child's position is the parent's position plus the child's own offset ROTATED by the parent's effective
 * rotation (always); its own rotation adds the parent's only with `rotateWithParent`. Cycles are refused (Phira
 * rejects them on load), so the parent picker and this command never create one.
 *
 * Modes:
 *  - 'keepNumbers' (RPE behaviour): the line's own values stay; it moves on screen.
 *  - 'keepPlace': the line stays where it is at `atSec`. Its world position under the old parent is converted into
 *    the new parent's frame, own = R(-parentRot) · (world - parentPos), and the difference to the current own sum is
 *    added to layer 0 of position X / Y. A track with at most one key is shifted as a whole (exact as long as nothing
 *    is animated); an animated position converts only the key at/after `atSec` (the last key when none is later),
 *    and the transaction notes it. Lines that turn with their parent also keep their on-screen angle.
 */

import { LINEAR } from '../ease/index';
import type { Line, LineId, LineProp } from '../model/types';
import {
  beatText,
  createTrack,
  isAncestorOrSelf,
  keysOf,
  lineLabel,
  lineOf,
  lineOwner,
  noNegZero,
  propSumAt,
  rotateVec,
  shiftKeyValue,
  tracksOfProp,
  worldPoseAt,
  type Tx,
} from './structureKit';
import { Beats } from '../time/beat';
import type { Sec } from '../time/types';
import type { LineCommands } from './types';

/** RPE-x px per chart unit (F§4). */
const PX_X = 675;
/** RPE-y px per chart unit before the aspect ratio (F§4). */
const PX_Y = 450;

/** True when `parent` may become the parent of `line` (no cycle). */
export function canParent(tx: Tx, line: LineId, parent: LineId | null): boolean {
  if (parent === null) return true;
  return !isAncestorOrSelf(tx, line, parent);
}

/**
 * Adds `delta` to the sum of a summed property at `atSec` through layer 0. Returns 'exact' when the property is not
 * animated (single keys or absent), else 'keyed' with the beat of the converted key.
 */
function shiftProp(
  tx: Tx,
  line: LineId,
  prop: Extract<LineProp, 'moveX' | 'moveY' | 'rotate'>,
  delta: number,
  atSec: Sec,
): { readonly mode: 'none' | 'exact' | 'keyed'; readonly beat?: string; readonly unlinked: boolean } {
  if (delta === 0 || !Number.isFinite(delta)) return { mode: 'none', unlinked: false };
  const animated = tracksOfProp(tx, line, prop).some((t) => tx.idx.keysByTrack(t).length > 1);
  const layer0 = tx.idx.track(line, prop, 0);
  if (layer0 === undefined) {
    createTrack(tx, lineOwner(line, prop, 0), 'number', [
      { t: Beats.ZERO, vIn: noNegZero(delta), vOut: noNegZero(delta), ease: LINEAR },
    ]);
    return { mode: animated ? 'keyed' : 'exact', beat: beatText(Beats.ZERO), unlinked: false };
  }
  const keys = keysOf(tx, layer0);
  if (keys.length === 1) {
    const only = keys[0];
    if (only !== undefined) shiftKeyValue(tx, only.id, delta);
    return {
      mode: animated ? 'keyed' : 'exact',
      ...(only !== undefined ? { beat: beatText(only.t) } : {}),
      unlinked: false,
    };
  }
  const target = keys.find((k) => tx.tempo.secAt(k.t) >= atSec) ?? keys[keys.length - 1];
  if (target === undefined) return { mode: 'none', unlinked: false };
  const unlinked = shiftKeyValue(tx, target.id, delta);
  return { mode: 'keyed', beat: beatText(target.t), unlinked };
}

const setParent: LineCommands['setParent'] = (tx: Tx, { line: lineId, parent, mode, atSec }) => {
  const line = lineOf(tx, lineId);
  if (parent !== null) {
    const p = lineOf(tx, parent);
    if (!canParent(tx, lineId, parent)) {
      tx.fail({ key: 'cmd.lines.parentCycle', params: { line: lineLabel(tx, line), parent: lineLabel(tx, p) } });
    }
  }
  if (line.parent === parent) return;
  if (mode === 'keepPlace') {
    if (!Number.isFinite(atSec)) tx.fail({ key: 'cmd.lines.badTime' });
    keepPlace(tx, line, parent, atSec);
  }
  tx.update('lines', lineId, { parent });
  tx.setFocus({ line: lineId });
};

function keepPlace(tx: Tx, line: Line, parent: LineId | null, atSec: Sec): void {
  const before = worldPoseAt(tx, line.id, atSec);
  const parentPose = parent === null ? { x: 0, y: 0, rot: 0 } : worldPoseAt(tx, parent, atSec);
  const [ox, oy] = rotateVec(before.x - parentPose.x, before.y - parentPose.y, -parentPose.rot);
  const asp = tx.doc.pkg.aspectRatio;
  const wantX = ox * PX_X;
  const wantY = oy * PX_Y * asp;
  const dx = wantX - propSumAt(tx, line.id, 'moveX', atSec);
  const dy = wantY - propSumAt(tx, line.id, 'moveY', atSec);
  const results = [
    shiftProp(tx, line.id, 'moveX', round6(dx), atSec),
    shiftProp(tx, line.id, 'moveY', round6(dy), atSec),
  ];
  if (line.rotateWithParent) {
    // Phira rot = -Σrotate + inherited; keep it: Σrotate' = Σrotate + inheritedNew - inheritedOld.
    const oldInherited = line.parent === null ? 0 : worldPoseAt(tx, line.parent, atSec).rot;
    const newInherited = parent === null ? 0 : parentPose.rot;
    results.push(shiftProp(tx, line.id, 'rotate', round6(newInherited - oldInherited), atSec));
  }
  const keyed = results.find((r) => r.mode === 'keyed');
  if (keyed !== undefined) {
    tx.note({ key: 'cmd.lines.keepPlaceAnimated', params: { line: lineLabel(tx, line), beat: keyed.beat ?? '0' } });
  }
  if (results.some((r) => r.unlinked)) tx.note({ key: 'cmd.lines.unlinkedForEdit' });
}

/** Rounds conversion noise (1e-6 RPE px is far below anything visible) so values stay readable. */
function round6(x: number): number {
  return noNegZero(Math.round(x * 1e6) / 1e6);
}

export const parentCommands = { setParent } as const;
